import { Avatar, Box, Checkbox, TableCell, TableRow, Typography } from '@mui/material';
import { Check, Globe, Plus } from 'lucide-react';
import { Button } from '@zaplify/ui';
import { stringToColor } from '../../../../helpers/utils';
import { ProspectDataDto } from '@zaplify/prospects';
import { TransactionState } from '../../../../enums/prospects';

export const ProspectsTableRow = ({
    row,
    handleClick,
    transactionState,
    selected,
    setSelectedExpandProspect,
    handlePurchaseClick,
}: {
    row: ProspectDataDto;
    handleClick: (e: any, id: string) => void;
    transactionState: TransactionState | undefined;
    selected: readonly string[];
    setSelectedExpandProspect: (row: ProspectDataDto) => void;
    handlePurchaseClick: (e: any, ids: string[]) => void;
}) => {
    return (
        <TableRow
            key={row.personId}
            sx={{
                '& th, td': {
                    background: 'white',
                    borderTop: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                    borderBottom: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                },
                '&:last-child td, &:last-child th': { border: 0 },
                '& td': {
                    color: '#3A4B59',
                    fontSize: '14px',
                    padding: '6px 12px',
                },
                '&:hover': {
                    '& .add-button': {
                        display: 'inline-flex',
                    },
                    '& th, td': {
                        background: 'var(--grey-palette-grey-1, #F7F8FA)',
                    },
                },
                cursor: 'pointer',
            }}
            onClick={() => setSelectedExpandProspect(row)}
        >
            <TableCell onClick={(e) => (transactionState ? null : handleClick(e, row.personId))}>
                {transactionState ? (
                    <Box sx={{ padding: '9px' }}>
                        <Check color="#6583EF" size={20} />
                    </Box>
                ) : (
                    <Checkbox
                        color="primary"
                        checked={selected.some((val) => val === row.personId)}
                        disabled={Boolean(transactionState)}
                    />
                )}
            </TableCell>

            <TableCell component="th" scope="row" sx={{ padding: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flex: '1 0 0',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            position: 'relative',
                        }}
                    >
                        <Avatar
                            sx={{
                                height: '32px',
                                width: '32px',
                                fontSize: '14px',
                                background: stringToColor(row.fullName),
                            }}
                        >
                            {row.fullName[0] + row.fullName.split(' ')[1][0]}
                        </Avatar>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-9px',
                                right: '-3px',
                                width: '16px',
                                height: '16px',
                                border: 'none',
                                borderRadius: '50%',
                                '& .MuiAvatar-root': {
                                    width: '16px',
                                    height: '16px',
                                },
                                '& .MuiSvgIcon-root': {
                                    width: '16px',
                                    height: '16px',
                                },
                            }}
                        ></Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: 'var(--no-spacing, 0px)',
                            flex: '1 0 0',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'start',
                                gap: '4px',
                                '& > span': {
                                    width: '18px',
                                    height: '18px',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#3A4B59',
                                    textAlign: 'left',
                                }}
                            >
                                {row.fullName}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#6B7985',
                            }}
                        >
                            {row.occupationTitle}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="right">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flex: '1 0 0',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            position: 'relative',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: '32px',
                                height: '32px',
                                background: stringToColor(row.fullName),
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-9px',
                                right: '-3px',
                                width: '16px',
                                height: '16px',
                                border: 'none',
                                borderRadius: '50%',
                                '& .MuiAvatar-root': {
                                    width: '16px',
                                    height: '16px',
                                },
                                '& .MuiSvgIcon-root': {
                                    width: '16px',
                                    height: '16px',
                                },
                            }}
                        ></Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: 'var(--no-spacing, 0px)',
                            flex: '1 0 0',
                            '& > p': {
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textAlign: 'left',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                '& span': {
                                    width: '18px',
                                    height: '18px',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#3A4B59',
                                    textAlign: 'left',
                                }}
                            >
                                {row.organizationName}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                '& a': {
                                    textDecoration: 'none',
                                    color: '#6583EF',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                            }}
                        >
                            <Globe strokeWidth={1} color="#6583EF" size={16} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: '#6583EF',
                                }}
                            ></Typography>
                        </Box>
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="left">{row.organizationIndustry}</TableCell>
            <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                {row.organizationSize}
            </TableCell>
            <TableCell
                className="location-cell"
                sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                }}
                align="left"
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: '4px',
                        position: 'relative',
                    }}
                >
                    <Typography
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            '-webkit-box-orient': 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#3A4B59',
                            fontSize: '14px',
                        }}
                    >
                        {row.location}
                    </Typography>

                    <Button
                        classes={['add-button', 'mint']}
                        sx={{ display: 'none', marginLeft: 'auto', position: 'absolute', right: '0', top: '0' }}
                        startIcon={<Plus strokeWidth={1} size={20} />}
                        disabled={Boolean(transactionState)}
                        height="32px"
                        onClick={(e) => handlePurchaseClick(e, [row.personId])}
                    >
                        Add
                    </Button>
                </Box>
            </TableCell>
        </TableRow>
    );
};
